import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getLang } from "store/userSlice";
import { errorMessage } from "helpers/common";
import { useForm } from "react-hook-form";
import { api } from "helpers/axios";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";
import { en, de } from 'yup-locales';
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Field, Input, Modal, NativeSelect } from "components/ui";
import { useTranslation } from "translation/useTranslation";

export default function Email() {
  const { translate, editableTranslate } = useTranslation();
  const lang = useSelector(getLang);

  const [error, setError] = useState(null);

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingTest, setLoadingTest] = useState(false);

  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmailError, setTestEmailError] = useState(null);

  const fetchEmailSetting = async () => {
    setError(null);

    try {
      const { data, status } = await api.get('/settings/email');
      if (status === 200) {
        reset(data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch email setting'));
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    fetchEmailSetting();
  }, []);


  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const smtpSchema = yup.object().shape({
    host: yup.string().required().label(translate('email.host')),
    port: yup.number().integer().positive().required().label(translate('email.port')),
    encryption: yup.string().required().label(translate('email.encryption')),
    username: yup.string().required().label(translate('email.username')),
    password: yup.string().required().label(translate('email.password')),
    sender_email: yup.string().email().required().label(translate('email.sender_email')),
    sender_name: yup.string().required().label(translate('email.sender_name'))
  });

  const schema = yup.object({
    transport: yup.string().required(),
    details: yup.object().when('transport', {
      is: (val) => val === 'smtp',
      then: () => smtpSchema,
      otherwise: () => yup.object().nullable()
    })
  });

  const { register, reset, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      transport: 'ses',
      details: {
        host: '',
        port: '',
        encryption: '',
        username: '',
        password: '',
        sender_email: '',
        sender_name: ''
      }
    }
  });

  const watchTransport = watch('transport');

  const onSubmit = async (data) => {
    setLoadingSave(true);
    setError(null);

    const formData = {
      transport: data.transport,
      ...(data.transport === 'smtp' && data.details)
    };

    try {
      const { status, data } = await api.patch(`/settings/email`, formData);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        reset(data);
      }
      setLoadingSave(false);
    } catch (err) {
      setLoadingSave(false);

      setError(errorMessage(err, 'save email setting'));
      Sentry.captureException(err);
    }
  };

  const testSchema = yup.object({
    email: yup.string().email().required().label(translate('field.email'))
  });

  const handleSubmitTest = async (e) => {
    e.preventDefault();
    setTestEmailError(null);

    const formData = { email: testEmail };

    try {
      await testSchema.validate(formData);
    } catch (err) {
      setTestEmailError(err.message);
      return;
    }

    setLoadingTest(true);

    try {
      const { status } = await api.post(`/settings/email/test`, formData);
      if (status === 200) {
        toast.success(translate('form.success'));
      }
      setTestEmail('');
      setShowTestModal(false);
      setLoadingTest(false);
    } catch (err) {
      setShowTestModal(false);
      setLoadingTest(false);

      setError(errorMessage(err, 'test email setting'));
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h4>{editableTranslate('global.menu.settings.email')}</h4>
          <p className="section-description">{editableTranslate('email.description')}</p>

          <div className="settings-batch-update mt-4">
            {
              error &&
              <div className="mt-3">
                <ErrorAlert error={error} onClose={() => setError(null)} />
              </div>
            }

            <div className="gray-box filters-box">
              <label className="question-label font-weight-bold">{editableTranslate('email.mailer')}</label>
              <div className="px-3 py-2 pb-3">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-row">
                    <div className="form-group col-4 mb-2">
                      <NativeSelect {...register('transport')} data-testid="transport-select">
                        <option value="ses">{translate('email.default')}</option>
                        <option value="smtp">{translate('email.custom_smtp')}</option>
                      </NativeSelect>
                    </div>
                  </div>
                  {
                    watchTransport === 'smtp' && <>
                      <div className="form-row mt-3">
                        <div className="form-group col-4">
                          <Field label={editableTranslate('email.host')} error={errors?.details?.host?.message}>
                            <Input type="text" error={!!errors?.details?.host} {...register('details.host')} maxLength={255} data-testid="details-host-input" />
                          </Field>
                        </div>
                        <div className="form-group col-4">
                          <Field label={editableTranslate('email.port')} error={errors?.details?.port?.message}>
                            <Input type="text" error={!!errors?.details?.port} {...register('details.port')} maxLength={6} data-testid="details-port-input" />
                          </Field>
                        </div>
                        <div className="form-group col-4">
                          <Field label={editableTranslate('email.encryption')} error={errors?.details?.encryption?.message}>
                            <NativeSelect error={!!errors?.details?.encryption} {...register('details.encryption')} data-testid="details-encryption-select">
                              <option value="ssl">SSL</option>
                              <option value="tls">TLS</option>
                            </NativeSelect>
                          </Field>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group col-4">
                          <Field label={editableTranslate('email.username')} error={errors?.details?.username?.message}>
                            <Input type="text" error={!!errors?.details?.username} {...register('details.username')} maxLength={255} data-testid="details-username-input" />
                          </Field>
                        </div>
                        <div className="form-group col-4">
                          <Field label={editableTranslate('email.password')} error={errors?.details?.password?.message}>
                            <Input type="text" error={!!errors?.details?.password} {...register('details.password')} maxLength={255} data-testid="details-password-input" />
                          </Field>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group col-4">
                          <Field label={editableTranslate('email.sender_email')} error={errors?.details?.sender_email?.message}>
                            <Input type="text" error={!!errors?.details?.sender_email} {...register('details.sender_email')} maxLength={255} data-testid="details-sender-email-input" />
                          </Field>
                        </div>
                        <div className="form-group col-4">
                          <Field label={editableTranslate('email.sender_name')} error={errors?.details?.sender_name?.message}>
                            <Input type="text" error={!!errors?.details?.sender_name} {...register('details.sender_name')} maxLength={255} data-testid="details-sender-name-input" />
                          </Field>
                        </div>
                      </div>
                    </>
                  }
                  <div className="text-right">
                    <Button variant="secondary" onClick={() => setShowTestModal(true)} data-testid="send-test-email-button" className="mr-2">{editableTranslate('email.send_test_email')}</Button>
                    <Button type="submit" variant="primary" loading={loadingSave} data-testid="save-button">{editableTranslate('button.save')}</Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showTestModal}
        size="sm"
        title={editableTranslate('email.send_test_email')}
        onClose={() => { setShowTestModal(false); setTestEmail(''); }}
        loading={loadingTest}
        testIdPrefix="test-"
        footer={
          <Button variant="primary" loading={loadingTest} onClick={handleSubmitTest} data-testid="proceed-button">
            {editableTranslate('button.proceed')}
          </Button>
        }
      >
        <Field label={editableTranslate('field.email')} error={testEmailError}>
          <Input data-testid="email-input" type="text" error={!!testEmailError} value={testEmail} onChange={e => setTestEmail(e.target.value)} maxLength={255} />
        </Field>
      </Modal>
    </>
  );
}
